export const SUBSCRIPTION_LIMITS = {
  trial: {
    name: "Trial",
    price: 0,
    maxUsers: 2, // Same as Single plan for trial
    maxPeople: 25, // People management limit
    maxLicenses: 120, // 120 license limit (same as Single plan)
    maxApplications: 50,
    features: {
      dashboard: true,
      basicReporting: true,
      licenseTracking: true,
      renewalAlerts: true,
      advancedAnalytics: false,
      bulkImport: true,
      apiAccess: false,
      prioritySupport: false,
      customIntegrations: false,
      sso: false,
      emailSupport: true
    }
  },
  single: {
    name: "Single",
    price: 29,
    maxUsers: 2, // Up to 2 users (including admin)
    maxPeople: 25, // Up to 25 people management
    maxLicenses: 120, // 120 license limit for Single plan
    maxApplications: 50, // Up to 50 applications
    features: {
      dashboard: true,
      basicReporting: true,
      licenseTracking: true,
      renewalAlerts: true,
      advancedAnalytics: false,
      bulkImport: true,
      apiAccess: false,
      prioritySupport: false,
      customIntegrations: false,
      sso: false,
      emailSupport: true
    }
  },
  team: {
    name: "Team",
    price: 49,
    maxUsers: 4, // Up to 4 users (including admin)
    maxPeople: 100, // Up to 100 people management
    maxLicenses: 250, // 250 license limit for Team plan
    maxApplications: 100, // Up to 100 applications
    features: {
      dashboard: true,
      basicReporting: true,
      licenseTracking: true,
      renewalAlerts: true,
      advancedAnalytics: true,
      bulkImport: true,
      apiAccess: true,
      prioritySupport: true,
      customIntegrations: false,
      sso: false,
      emailSupport: true,
      advancedDashboard: true,
      teamManagement: true
    }
  },
  enterprise: {
    name: "Enterprise",
    price: 199,
    maxUsers: 15, // Up to 15 users (including admin)
    maxPeople: 999999, // Unlimited people management
    maxLicenses: 999999, // Unlimited licenses
    maxApplications: 999999, // Unlimited applications
    features: {
      dashboard: true,
      basicReporting: true,
      licenseTracking: true,
      renewalAlerts: true,
      advancedAnalytics: true,
      bulkImport: true,
      apiAccess: true,
      prioritySupport: true,
      customIntegrations: true,
      sso: true,
      emailSupport: true,
      advancedDashboard: true,
      teamManagement: true,
      customReporting: true,
      dedicatedSupport: true,
      enterpriseDashboard: true,
      advancedTeamManagement: true
    }
  }
} as const;

export type PlanType = keyof typeof SUBSCRIPTION_LIMITS;

export function getPlanLimits(planType: PlanType) {
  return SUBSCRIPTION_LIMITS[planType];
}

export function hasFeature(planType: PlanType, feature: keyof typeof SUBSCRIPTION_LIMITS.trial.features) {
  return SUBSCRIPTION_LIMITS[planType].features[feature];
}

export function canAddUser(planType: PlanType, currentUserCount: number) {
  const limits = getPlanLimits(planType);
  return currentUserCount < limits.maxUsers;
}

export function canAddLicense(planType: PlanType, currentLicenseCount: number) {
  const limits = getPlanLimits(planType);
  return currentLicenseCount < limits.maxLicenses;
}

export function canAddApplication(planType: PlanType, currentApplicationCount: number) {
  const limits = getPlanLimits(planType);
  return currentApplicationCount < limits.maxApplications;
}

export function canAddPeople(planType: PlanType, currentPeopleCount: number) {
  const limits = getPlanLimits(planType);
  return currentPeopleCount < limits.maxPeople;
}

export function getFeatureList(planType: PlanType): string[] {
  const plan = getPlanLimits(planType);
  const features: string[] = [];
  
  if (planType === 'trial') {
    features.push(`Up to ${plan.maxUsers} users (including admin)`);
    features.push(`Up to ${plan.maxPeople} people management`);
    features.push(`Up to ${plan.maxApplications} applications`);
    features.push('Basic reporting');
    features.push('14-day trial');
    return features;
  }

  features.push(`Up to ${plan.maxUsers} users (including admin)`);
  
  if (plan.maxPeople === 999999) {
    features.push('Unlimited people management');
  } else {
    features.push(`Up to ${plan.maxPeople} people management`);
  }
  
  if (plan.maxApplications === 999999) {
    features.push('Unlimited SaaS applications');
  } else {
    features.push(`Up to ${plan.maxApplications} SaaS applications`);
  }
  
  if (plan.maxLicenses === 999999) {
    features.push('Unlimited licenses');
  } else {
    features.push(`${plan.maxLicenses} license limit`);
  }

  if (plan.features.basicReporting) features.push('Basic reporting');
  if (plan.features.advancedAnalytics) features.push('Advanced reporting');
  if (plan.features.emailSupport) features.push('Email support');
  if (plan.features.prioritySupport) features.push('Priority email support');
  if (plan.features.renewalAlerts) features.push('License renewal tracking');

  return features;
}